import {
  BaseLinkData,
  BaseLinkLabelData,
  ModelConceptClass,
  ModelConceptHierarchy,
  ModelConceptHierarchyLabel,
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  isDeletedMapping,
  isDeletedMappingLabel,
  isDeletedModelConceptClass,
  isDeletedModelConceptHierarchy,
  isDeletedModelConceptHierarchyLabel,
  isDeletedModelConceptRelationship,
  isDeletedModelConceptRelationshipLabel,
  isDeletedRelationshipMapping,
  isMapping,
  isMappingLabel,
  isModelConceptHierarchy,
  isModelConceptHierarchyLabel,
  isModelConceptRelationship,
  isModelConceptRelationshipLabel,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  getDeletedModelConceptClassValue,
  isDeletedModelConceptClassKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptClass/createDeletedModelConceptClass';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';

const restoreDeletedKeys = (
  model: Model,
  deletedKey: string,
  addedKey: string
) => {
  const replaceKeysForPreviouslyDeletedLink = (
    linkData: BaseLinkData | BaseLinkLabelData
  ) => {
    if (linkData.from === deletedKey) {
      setDataProperty<BaseLinkData | BaseLinkLabelData>(model)(
        linkData,
        'from',
        addedKey
      );
    }
    if (linkData.to === deletedKey) {
      setDataProperty<BaseLinkData | BaseLinkLabelData>(model)(
        linkData,
        'to',
        addedKey
      );
    }
  };

  model.nodeDataArray.forEach((nodeData) => {
    if (
      isModelConceptHierarchyLabel(nodeData) ||
      isModelConceptRelationshipLabel(nodeData) ||
      isMappingLabel(nodeData) ||
      isDeletedModelConceptHierarchyLabel(nodeData) ||
      isDeletedModelConceptRelationshipLabel(nodeData) ||
      isDeletedMappingLabel(nodeData)
    ) {
      replaceKeysForPreviouslyDeletedLink(nodeData);
    }
  });
  model.linkDataArray.forEach((linkData) => {
    if (
      isModelConceptHierarchy(linkData) ||
      isModelConceptRelationship(linkData) ||
      isMapping(linkData) ||
      isRelationshipMapping(linkData) ||
      isDeletedModelConceptHierarchy(linkData) ||
      isDeletedModelConceptRelationship(linkData) ||
      isDeletedMapping(linkData) ||
      isDeletedRelationshipMapping(linkData)
    ) {
      replaceKeysForPreviouslyDeletedLink(linkData);
    }
  });
};

const compareOriginalIris =
  (originalIris: Record<string, string>) =>
  (prevIri: string, nextIri: string) => {
    return (originalIris[prevIri] || prevIri) === nextIri;
  };

const compareClassKeys =
  (compareIris: (prevIri: string, nextIri: string) => boolean) =>
  (prevKey?: string, nextKey?: string) => {
    if (!prevKey && !nextKey) {
      return true;
    }
    if (!prevKey || !nextKey) {
      return false;
    }

    const prevIri = isDeletedModelConceptClassKey(prevKey)
      ? getDeletedModelConceptClassValue(prevKey)
      : getModelConceptClassValue(prevKey);
    const nextIri = isDeletedModelConceptClassKey(nextKey)
      ? getDeletedModelConceptClassValue(nextKey)
      : getModelConceptClassValue(nextKey);
    return compareIris(prevIri, nextIri);
  };

export const restoreDeletedClass =
  (data: ModelConceptClass) => (model: Model) => {
    if (!model.modelData.draftData) {
      return;
    }

    const compareIri = compareOriginalIris(
      model.modelData.draftData.originalIrisByCurrentIri
    );

    const deleted = model.nodeDataArray.find(
      (nodeData) =>
        isDeletedModelConceptClass(nodeData) &&
        compareIri(nodeData.value, data.value)
    );
    if (!deleted) {
      return;
    }

    restoreDeletedKeys(model, deleted.key, data.key);
    model.removeNodeData(deleted);
  };

export const restoreDeletedHierarchy =
  (data: ModelConceptHierarchy, dataLabel: ModelConceptHierarchyLabel) =>
  (model: Model) => {
    if (!model.modelData.draftData) {
      return;
    }

    const compareIri = compareOriginalIris(
      model.modelData.draftData.originalIrisByCurrentIri
    );
    const compareKeys = compareClassKeys(compareIri);

    const deleted = model.linkDataArray.find(
      (linkData) =>
        isDeletedModelConceptHierarchy(linkData) &&
        compareIri(linkData.value, data.value) &&
        compareKeys(linkData.from, data.from) &&
        compareKeys(linkData.to, data.to)
    );
    if (!deleted) {
      return;
    }

    const deletedLabel = model.nodeDataArray.find(
      (nodeData) =>
        isDeletedModelConceptHierarchyLabel(nodeData) &&
        compareIri(nodeData.value, dataLabel.value) &&
        compareKeys(nodeData.from, dataLabel.from) &&
        compareKeys(nodeData.to, dataLabel.to)
    );
    if (!deletedLabel) {
      return;
    }

    restoreDeletedKeys(model, deletedLabel.key, dataLabel.key);
    model.removeNodeData(deletedLabel);
    model.removeLinkData(deleted);
  };

export const restoreDeletedRelationship =
  (data: ModelConceptRelationship, dataLabel: ModelConceptRelationshipLabel) =>
  (model: Model) => {
    if (!model.modelData.draftData) {
      return;
    }

    const compareIri = compareOriginalIris(
      model.modelData.draftData.originalIrisByCurrentIri
    );
    const compareKeys = compareClassKeys(compareIri);

    const deleted = model.linkDataArray.find(
      (linkData) =>
        isDeletedModelConceptRelationship(linkData) &&
        compareIri(linkData.value, data.value) &&
        compareKeys(linkData.from, data.from) &&
        compareKeys(linkData.to, data.to)
    );
    if (!deleted) {
      return;
    }

    const deletedLabel = model.nodeDataArray.find(
      (nodeData) =>
        isDeletedModelConceptRelationshipLabel(nodeData) &&
        compareIri(nodeData.value, dataLabel.value) &&
        compareKeys(nodeData.from, dataLabel.from) &&
        compareKeys(nodeData.to, dataLabel.to)
    );
    if (!deletedLabel) {
      return;
    }

    restoreDeletedKeys(model, deletedLabel.key, dataLabel.key);
    model.removeNodeData(deletedLabel);
    model.removeLinkData(deleted);
  };
